/**
 * WorkoutLogger — interactive strength session: pick exercise → log each set's
 * reps → rest countdown → repeat → finish exercise → next exercise → save.
 */
import { useEffect, useRef, useState } from 'react';
import { Dumbbell, Plus, Check, X, Play, Timer, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { nexaApi } from './api';

const EXERCISES = ['Pull-ups', 'Dips', 'Push-ups', 'Squats', 'Crunches'];
const REST_OPTIONS = [30, 60, 90, 120];
const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

const beep = () => {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.frequency.value = 880; o.type = 'sine';
    g.gain.setValueAtTime(0.001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
    o.start(); o.stop(ctx.currentTime + 0.55);
  } catch { /* ignore */ }
  try { navigator.vibrate?.([200, 80, 200]); } catch { /* ignore */ }
};

export const WorkoutLogger = ({ onSaved }) => {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState([]);       // [{name, sets:[reps]}]
  const [current, setCurrent] = useState(null); // exercise name
  const [sets, setSets] = useState([]);        // reps for current exercise
  const [reps, setReps] = useState('');
  const [bests, setBests] = useState({});
  const [pbIdx, setPbIdx] = useState(new Set());
  const [rest, setRest] = useState(60);
  const [left, setLeft] = useState(0);         // seconds remaining
  const [saving, setSaving] = useState(false);
  const tick = useRef(null);

  useEffect(() => () => clearInterval(tick.current), []);

  const start = () => {
    setOpen(true); setDone([]); setCurrent(null); setSets([]); setReps(''); setLeft(0); setPbIdx(new Set());
    nexaApi.get('/workouts/bests').then(({ data }) => setBests(data.bests || {})).catch(() => setBests({}));
  };
  const close = () => { clearInterval(tick.current); setOpen(false); };

  const addSet = () => {
    const n = parseInt(reps, 10);
    if (!n || n <= 0) { toast.error('Enter reps'); return; }
    const idx = sets.length;
    if (n > (bests[current] || 0)) {
      setBests((b) => ({ ...b, [current]: n }));
      setPbIdx((p) => new Set(p).add(idx));
      toast.success(`🔥 New personal best — ${n} ${current}!`);
    }
    setSets((s) => [...s, n]);
    setReps('');
    startRest();
  };
  const startRest = () => {
    clearInterval(tick.current);
    setLeft(rest);
    tick.current = setInterval(() => {
      setLeft((l) => {
        if (l <= 1) { clearInterval(tick.current); beep(); return 0; }
        return l - 1;
      });
    }, 1000);
  };
  const stopRest = () => { clearInterval(tick.current); setLeft(0); };

  const finishExercise = () => {
    if (current && sets.length) setDone((d) => [...d, { name: current, sets }]);
    setCurrent(null); setSets([]); setReps(''); stopRest();
  };

  const finishWorkout = async () => {
    const all = [...done];
    if (current && sets.length) all.push({ name: current, sets });
    if (!all.length) { toast.error('Log at least one set'); return; }
    setSaving(true);
    try {
      await nexaApi.post('/workouts', { exercises: all });
      toast.success('Workout saved 💪');
      close();
      onSaved?.();
    } catch { toast.error('Could not save workout'); } finally { setSaving(false); }
  };

  const totalSets = done.reduce((a, e) => a + e.sets.length, 0) + sets.length;

  return (
    <>
      <button onClick={start} data-testid="nexa-start-workout"
        className="w-full rounded-2xl border border-sky-400/30 bg-sky-400/10 p-4 flex items-center justify-center gap-2.5 text-sky-200 active:scale-[0.99] transition-transform">
        <Dumbbell className="w-5 h-5" strokeWidth={1.8} />
        <span className="text-[14px] font-medium">Start workout</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6" data-testid="nexa-wl-modal">
            <motion.div initial={{ scale: 0.96, y: 12 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-md max-h-[92vh] flex flex-col rounded-2xl border border-white/12 bg-[#0b0b0f]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                <div>
                  <p className="text-[13.5px] font-semibold text-white/90">Workout</p>
                  <p className="text-[11px] text-white/45">{done.length} exercise{done.length === 1 ? '' : 's'} · {totalSets} set{totalSets === 1 ? '' : 's'}</p>
                </div>
                <button onClick={close} data-testid="nexa-wl-close" className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/80 hover:bg-white/20"><X className="w-4 h-4" /></button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
                {/* Completed exercises */}
                {done.map((e, i) => (
                  <div key={i} className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2.5" data-testid={`nexa-wl-done-${i}`}>
                    <div className="flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-400" />
                      <span className="text-[13px] text-white/85 font-medium">{e.name}</span>
                      <span className="text-[12px] text-white/45 ml-auto">{e.sets.join(' · ')}</span>
                    </div>
                  </div>
                ))}

                {!current ? (
                  <div>
                    <p className="text-[11px] font-mono tracking-[0.2em] uppercase text-white/45 mb-2">Choose an exercise</p>
                    <div className="grid grid-cols-1 gap-2">
                      {EXERCISES.map((ex) => (
                        <button key={ex} onClick={() => { setCurrent(ex); setSets([]); setReps(''); setPbIdx(new Set()); stopRest(); }}
                          data-testid={`nexa-wl-ex-${ex}`}
                          className="flex items-center justify-between rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-[13.5px] text-white/85 hover:bg-white/[0.08] active:scale-[0.99]">
                          <span>{ex}{bests[ex] ? <span className="text-white/40 text-[12px] ml-2">best {bests[ex]}</span> : null}</span> <ChevronRight className="w-4 h-4 text-white/40" />
                        </button>
                      ))}
                    </div>
                    {(done.length > 0) && (
                      <button onClick={finishWorkout} disabled={saving} data-testid="nexa-wl-finish-workout"
                        className="w-full mt-4 h-11 rounded-xl bg-emerald-400 text-black font-semibold text-[13.5px] disabled:opacity-50 active:scale-[0.98]">
                        {saving ? 'Saving…' : 'Finish & save workout'}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-sky-400/25 bg-sky-400/[0.05] p-4">
                    <p className="text-[15px] font-semibold text-white/90 mb-2">{current}<span className="text-white/40 text-[12px] font-normal ml-2">{bests[current] ? `best ${bests[current]}` : 'no PB yet'}</span></p>
                    {sets.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="nexa-wl-sets">
                        {sets.map((s, i) => (
                          <span key={i} data-testid={`nexa-wl-set-${i}`}
                            className={`text-[12px] px-2.5 py-1 rounded-lg ${pbIdx.has(i) ? 'bg-amber-400/20 text-amber-200 border border-amber-400/40' : 'bg-white/[0.06] text-white/75'}`}>
                            {pbIdx.has(i) ? '🔥 ' : ''}Set {i + 1}: {s}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* rest timer running */}
                    {left > 0 ? (
                      <div className="text-center py-4" data-testid="nexa-wl-timer">
                        <div className="nexa-heading text-5xl font-light text-sky-300 tabular-nums">{fmt(left)}</div>
                        <p className="text-[11px] text-white/40 mt-1">Rest — next set when you're ready</p>
                        <button onClick={stopRest} data-testid="nexa-wl-skip-rest" className="mt-3 text-[12px] text-white/60 underline">Skip rest</button>
                      </div>
                    ) : (
                      <>
                        <div className="flex gap-2 items-center mb-3">
                          <input type="number" inputMode="numeric" value={reps} onChange={(e) => setReps(e.target.value)}
                            placeholder="reps" data-testid="nexa-wl-reps"
                            className="flex-1 bg-white/[0.05] border border-white/10 rounded-xl px-3 py-2.5 text-[15px] text-white/90 outline-none focus:border-sky-400/40" />
                          <button onClick={addSet} data-testid="nexa-wl-add-set"
                            className="h-11 px-4 rounded-xl bg-sky-400 text-black font-semibold text-[13px] flex items-center gap-1.5 active:scale-95"><Plus className="w-4 h-4" />Add set</button>
                        </div>
                        <div className="flex items-center gap-1.5 mb-1">
                          <Timer className="w-3.5 h-3.5 text-white/40" />
                          <span className="text-[11px] text-white/40">Rest timer</span>
                        </div>
                        <div className="grid grid-cols-4 gap-1.5">
                          {REST_OPTIONS.map((r) => (
                            <button key={r} onClick={() => setRest(r)} data-testid={`nexa-wl-rest-${r}`}
                              className={`h-9 rounded-lg text-[12px] font-medium transition-colors ${rest === r ? 'bg-sky-400 text-black' : 'text-white/55 bg-white/[0.04] border border-white/10'}`}>{fmt(r)}</button>
                          ))}
                        </div>
                        {sets.length > 0 && (
                          <button onClick={startRest} data-testid="nexa-wl-rest-start"
                            className="w-full mt-2 h-9 rounded-lg border border-white/12 text-[12.5px] text-white/70 flex items-center justify-center gap-1.5"><Play className="w-3.5 h-3.5" />Start {fmt(rest)} rest</button>
                        )}
                      </>
                    )}

                    <button onClick={finishExercise} data-testid="nexa-wl-finish-ex"
                      className="w-full mt-3 h-10 rounded-xl border border-white/15 text-[13px] text-white/80 hover:bg-white/5 active:scale-[0.98]">
                      Done with {current}
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WorkoutLogger;
